'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { fetchDynamicSections } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import { sendPriceOfferMessage as sendPriceOfferMessageCore } from '@eliadmin/core/src/api/forms/forms-api';
import { CaptchaAction } from '@eliadmin/core/src/components/providers/captcha-provider';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  ResponsiveDialogContent,
  useResponsiveDialogStore,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';

import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { FiSend } from 'react-icons/fi';
import { z } from 'zod';
import type { TDynamicData } from '../../types/dynamic-data';
const sendPriceOfferMessage = (req: Parameters<typeof sendPriceOfferMessageCore>[0]) => sendPriceOfferMessageCore({ ...req, captchaAction: CaptchaAction.PRICE_OFFER_CONTACT });

const nearStreamOptions = [
  { value: 'Áno', label: { sk: 'Áno', de: 'Ja' } },
  { value: 'Nie', label: { sk: 'Nie', de: 'Nein' } },
];

const slopedLandOptions = [
  { value: 'Mám', label: { sk: 'Mám', de: 'Ja' } },
  { value: 'Nemám', label: { sk: 'Nemám', de: 'Nein' } },
  { value: 'Neviem', label: { sk: 'Neviem', de: 'Ich weiß nicht' } },
];

const sendPriceCalculatorMessage = async (req: {
  data: PriceCalculatorFormValues;
  locale: string;
}) => {
  return sendPriceOfferMessage(req);
};

const priceCalculatorSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: z
      .string()
      .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE())
      .refine(val => /^\+?[0-9\s]*$/.test(val), {
        message: VM.PHONE_INVALID_VALIDATION_MESSAGE(),
      }),
    text: z.string(),
    place_of_execution: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    interested_in: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    near_a_stream: z.enum(['Áno', 'Nie']),
    sloping_plot: z.enum(['Mám', 'Nemám', 'Neviem']),
    place_treat_wastewater: z.string().optional().default('ine'),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    people_count: z
      .number({ invalid_type_error: VM.INVALID_NUMBER_VALIDATION_MESSAGE() })
      .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    'g-recaptcha-response': z.string(),
  });

export type PriceCalculatorFormValues = z.infer<ReturnType<typeof priceCalculatorSchema>>;

export function PriceCalculator() {
  const formId = useId();
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<PriceCalculatorFormValues>({
    resolver: zodResolver(priceCalculatorSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      text: '',
      phone: '',
      place_of_execution: '',
      interested_in: '',
      near_a_stream: 'Áno',
      people_count: 2,
      sloping_plot: 'Mám',

      gdpr: false,
      'g-recaptcha-response': '',
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendPriceCalculatorMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const placeholders =
    locale === 'de'
      ? {
          surname: 'Nachname...',
          email: 'E-Mail...',
          phone: 'Telefon...',
          message: 'Nachricht...',
          place: 'Standort der Umsetzung...',
          select: 'Auswählen..',
          count: 'Anzahl eingeben...',
        }
      : {
          surname: 'Priezvisko...',
          email: 'Email...',
          phone: 'Telefón...',
          message: 'Správa...',
          place: 'Miesto realizácie...',
          select: 'Vyberte..',
          count: 'Zadajte počet...',
        };

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({
        data: {
          ...data,
          // biome-ignore lint/style/noNonNullAssertion: <explanation>

        },
        locale,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });

      // form submitted successfully, mark in gtag
      if (window.gtag) {
        window.gtag('event', 'submit', {
          event_category: 'form',
          event_label: 'price_calculator',
        });
      }

      if (window.ga) {
        window.ga('send', 'event', 'form', 'submit', 'price_calculator');
      }

      if (window.fbq) {
        window.fbq('track', 'Lead');
      }

      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, {
            type: 'manual',
            message: errors.join('\n'),
          });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  const { data: interestedOptionsData = [] } = useQuery({
    queryKey: [CacheKey.SLUG_DETAIL, locale],
    queryFn: () => fetchDynamicSections<TDynamicData>({ locale, sectionIds: [5] }),
  });

  const interestedOptions = interestedOptionsData.map(option => ({
    value: `${option.name}`,
    label: `${option.name}`,
  }));

  return (
    <div className="flex w-full max-w-[600px] flex-col items-center justify-center rounded-md border border-border">
      <div className="flex w-full items-center justify-between border-b-2 px-8 py-4 sm:border-none">
        <h1 className="w-full text-center text-3xl font-bold text-[#333333]">{t('text3905')}</h1>
      </div>
      <Form {...form}>
        <div className="w-full bg-neutral-100">
          <form
            onSubmit={handleSubmit}
            className={cn(
              'mb-8 grid min-h-fit w-full max-w-[600px] grid-cols-2 gap-x-4 gap-y-6 p-4',
              'ea-price-calculator-form__form',
            )}
          >
            <FormField
              control={form.control}
              name={'name'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn(
                    'col-span-2 flex w-full flex-col items-start justify-start gap-1.5 sm:col-span-1',
                    'ea-contact-form__field ea-contact-form__name-field',
                  )}
                >
                  <FormLabel>{t('text33')}</FormLabel>
                  <div
                    className={cn(
                      'w-full',
                      'ea-price-calculator-form__input-wrapper ea-price-calculator-form__name-input-wrapper',
                    )}
                  >
                    <FormControl>
                      <Input
                        {...(field as any)}
                        placeholder={t('text33') + '...'}
                        className={cn(
                          'rounded-[6px]',
                          'ea-price-calculator__form ea-price-calculator__input ',
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'surname'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn(
                    'col-span-2 flex w-full flex-col items-start justify-start gap-1.5 sm:col-span-1',
                    'ea-contact-form__field ea-contact-form__name-field',
                  )}
                >
                  <FormLabel>{t('text34')}</FormLabel>
                  <div
                    className={cn(
                      'w-full',
                      'ea-price-calculator-form__input-wrapper ea-price-calculator-form__name-input-wrapper',
                    )}
                  >
                    <FormControl>
                      <Input
                        {...(field as any)}
                        placeholder={placeholders.surname}
                        className={cn(
                          'rounded-[6px]',
                          'ea-price-calculator__form ea-price-calculator__input ',
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'email'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn(
                    'col-span-2 flex w-full flex-col items-start justify-start gap-1.5 sm:col-span-1',
                    'ea-contact-form__field ea-contact-form__email-field',
                  )}
                >
                  <FormLabel>
                    <span>{t('text35')}</span>
                  </FormLabel>
                  <div
                    className={cn(
                      'w-full',
                      'ea-price-calculator-form__input-wrapper ea-price-calculator-form__name-input-wrapper',
                    )}
                  >
                    <FormControl>
                      <Input
                        {...(field as any)}
                        placeholder={placeholders.email}
                        className={cn(
                          'rounded-[6px]',
                          'ea-price-calculator__form ea-price-calculator__input ',
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'phone'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn(
                    'col-span-2 flex w-full flex-col items-start justify-start gap-1.5 sm:col-span-1',
                    'ea-contact-form__field ea-contact-form__name-field',
                  )}
                >
                  <FormLabel>
                    <span>{t('text36')}</span>
                  </FormLabel>
                  <div
                    className={cn(
                      'w-full',
                      'ea-price-calculator-form__input-wrapper ea-price-calculator-form__name-input-wrapper',
                    )}
                  >
                    <FormControl>
                      <Input
                        {...(field as any)}
                        placeholder={placeholders.phone}
                        className={cn(
                          'rounded-[6px]',
                          'ea-price-calculator__form ea-price-calculator__input ',
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'text'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn(
                    'col-span-2 flex w-full flex-col items-start justify-start gap-1.5',
                    'ea-contact-form__field ea-contact-form__text-field',
                  )}
                >
                  <FormLabel>
                    <span>{t('text615')}</span>
                  </FormLabel>
                  <div
                    className={cn(
                      'flex w-full flex-row items-center space-x-2',
                      'ea-contact-form__input-wrapper ea-contact-form__text-input-wrapper',
                    )}
                  >
                    <FormControl>
                      <Textarea
                        placeholder={placeholders.message}
                        {...(field as any)}
                        rows={5}
                        className="w-full rounded-[6px]"
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'place_of_execution'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn(
                    'col-span-2 flex w-full flex-col items-start justify-start gap-1.5',
                    'ea-contact-form__field ea-contact-form__name-field',
                  )}
                >
                  <FormLabel>
                    <span>{t('text1138')}</span>
                  </FormLabel>
                  <div
                    className={cn(
                      'w-full',
                      'ea-price-calculator-form__input-wrapper ea-price-calculator-form__place-input-wrapper',
                    )}
                  >
                    <FormControl>
                      <Input
                        {...(field as any)}
                        placeholder={placeholders.place}
                        className={cn(
                          'rounded-[6px]',
                          'ea-price-calculator__form ea-price-calculator__input ',
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'interested_in'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn(
                    'col-span-2 flex w-full flex-col items-start justify-start gap-1.5',
                    'ea-contact-form__field ea-contact-form__name-field',
                  )}
                >
                  <FormLabel>
                    <span>{t('text1139')}</span>
                  </FormLabel>
                  <div
                    className={cn(
                      'w-full',
                      'ea-price-calculator-form__input-wrapper ea-price-calculator-form__place-input-wrapper',
                    )}
                  >
                    <Select
                      value={String(field.value)}
                      onValueChange={e => {
                        field.onChange(e);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger aria-label={t('text1139')}>
                          <SelectValue placeholder={placeholders.select} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {interestedOptions?.map(o => (
                          <SelectItem
                            key={o.value}
                            value={String(o.value)}
                            className={cn('ea-price-calculator-form__item')}
                          >
                            {t(o.label as any)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'near_a_stream'}
              render={({ field }) => {
                return (
                  <FormItem required={true} className="flex flex-col gap-2 p-[1px]">
                    <FormLabel>{t('text3916')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        aria-label={t('text3916')}
                        value={String(field.value)}
                        onValueChange={field.onChange}
                        className="flex flex-row gap-4 pl-2"
                      >
                        {nearStreamOptions?.map(o => (
                          <div key={o.value.toString()} className="flex gap-2">
                            <RadioGroupItem
                              id={`${formId}-near-stream-${o.value}`}
                              value={o.value.toString()}
                            />
                            <Label
                              className="flex items-center"
                              htmlFor={`${formId}-near-stream-${o.value}`}
                            >
                              <p className="text-sm font-semibold text-foreground">
                                {locale === 'de' ? o.label.de : o.label.sk}
                              </p>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name={'people_count'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn(
                    'col-span-2 flex w-full flex-col items-start justify-start gap-1.5',
                    'ea-contact-form__field ea-contact-form__name-field',
                  )}
                >
                  <FormLabel>
                    <span>{t('text3917')}</span>
                  </FormLabel>
                  <div
                    className={cn(
                      'w-full',
                      'ea-price-calculator-form__input-wrapper ea-price-calculator-form__name-input-wrapper',
                    )}
                  >
                    <FormControl>
                      <Input
                        {...(field as any)}
                        placeholder={placeholders.count}
                        onChange={e => {
                          const value = e.target.value;
                          if (value === '') {
                            field.onChange('');
                          } else {
                            const numberValue = Number(value);
                            if (!isNaN(numberValue)) {
                              field.onChange(numberValue);
                            }
                          }
                        }}
                        className={cn(
                          'rounded-[6px]',
                          'ea-price-calculator__form ea-price-calculator__input ',
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'sloping_plot'}
              render={({ field }) => {
                return (
                  <FormItem required={true} className="flex flex-col gap-2 p-[1px]">
                    <FormLabel>{t('text3918')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        aria-label={t('text3918')}
                        value={String(field.value)}
                        onValueChange={field.onChange}
                        className="flex flex-row gap-4 pl-2"
                      >
                        {slopedLandOptions?.map(o => (
                          <div key={o.value?.toString() || 'null'} className="flex gap-2">
                            <RadioGroupItem id={`${formId}-sloping-plot-${o.value}`} value={o.value} />
                            <Label
                              className="flex items-center"
                              htmlFor={`${formId}-sloping-plot-${o.value}`}
                            >
                              <p className="text-sm font-semibold text-foreground">
                                {locale === 'de' ? o.label.de : o.label.sk}
                              </p>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="gdpr"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'col-span-2 flex items-center gap-2',
                    'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
                  )}
                >
                  <FormControl>
                    <Checkbox
                      {...(field as any)}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className={cn(
                        'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
                        'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox',
                      )}
                    />
                  </FormControl>
                  <FormLabel className={cn('', 'ea-contact-form__gdpr-checkbox-label')}>
                    {t('text613')}{' '}
                    <Link
                      href={ROUTES.GDPR()}
                      className={cn('', 'ea-contact-form__gdpr-link')}
                      prefetch={false}
                    >
                      <span className="text-primary">{t('text614')}</span>
                    </Link>
                  </FormLabel>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'g-recaptcha-response'}
              render={() => (
                <FormItem required className={cn('col-span-2')}>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              variant={CONTACT_CONFIG.form.submit.variant}
              className={cn('col-span-2', 'ea-contact-form__submit')}
              isLoading={form.formState.isSubmitting}
            >
              <FiSend className="mr-2 h-5 w-5" />
              {t('text185')}
            </Button>
          </form>
        </div>
      </Form>
    </div>
  );
}
